import type { Benefit } from '@pflegia/types/api/benefit';
import type { Employer } from '@pflegia/types/api/employer/employer';
import type { GeoLocation } from '@pflegia/types/api/location';
import { ScrollableSectionEnum } from '@pflegia/types/common/enums/scrollable-section-enum';

export type BaseJobPosition = {
  employer?: Pick<Employer, 'aboutUs' | 'gallery'> | null;
  responsibilities?: string[];
  benefits?: Benefit[];
  salaryLow?: number | null;
  salaryHigh?: number | null;
  additionalSalaryInformation?: string | null;
  showSalary?: boolean;
  candidateProfile?: string[];
  geoLocation?: GeoLocation;
  onboardingProcedure?: string | null;
};

type GetSectionListFunction<T> = (jobPosition: T) => Partial<Record<ScrollableSectionEnum, boolean>>;

export const buildSectionList = <T extends BaseJobPosition>(
  jobPosition: T,
  getCustomSections?: GetSectionListFunction<T>,
  extraVisibilityRules: Partial<Record<ScrollableSectionEnum, boolean>> = {},
  sectionOrder: ScrollableSectionEnum[] = [],
): {
  sections: ScrollableSectionEnum[];
  visibleSections: Record<ScrollableSectionEnum, boolean>;
} => {
  const common: Partial<Record<ScrollableSectionEnum, boolean>> = {
    [ScrollableSectionEnum.Summary]: true,
    [ScrollableSectionEnum.Location]: extraVisibilityRules[ScrollableSectionEnum.Location] ?? Boolean(jobPosition.geoLocation),
    [ScrollableSectionEnum.AboutUs]:
      (extraVisibilityRules[ScrollableSectionEnum.AboutUs] ?? true) && Boolean(jobPosition.employer?.aboutUs),
    [ScrollableSectionEnum.Salary]:
      (extraVisibilityRules[ScrollableSectionEnum.Salary] ?? true) &&
      Boolean(jobPosition?.showSalary) &&
      (jobPosition.salaryHigh !== null || jobPosition.salaryLow !== null || jobPosition.additionalSalaryInformation !== null),
    [ScrollableSectionEnum.Profile]: (jobPosition.candidateProfile ?? []).length > 0,
    [ScrollableSectionEnum.Responsibilities]: (jobPosition.responsibilities ?? []).length > 0,
    [ScrollableSectionEnum.Benefits]: Boolean(jobPosition.benefits),
    [ScrollableSectionEnum.Onboarding]: Boolean(jobPosition.onboardingProcedure?.length),
    [ScrollableSectionEnum.Gallery]: (jobPosition.employer?.gallery ?? []).length > 0,
    [ScrollableSectionEnum.Career]: true,
  };

  const custom = getCustomSections?.(jobPosition) ?? {};
  const combinedSections: Record<ScrollableSectionEnum, boolean> = {
    ...(custom as Record<ScrollableSectionEnum, boolean>),
    ...(common as Record<ScrollableSectionEnum, boolean>),
  };

  const visibleSections: Record<ScrollableSectionEnum, boolean> = Object.fromEntries(
    Object.entries(combinedSections)
      .filter(([key, _]) => Object.values(ScrollableSectionEnum).includes(key as ScrollableSectionEnum))
      .sort(([a], [b]) => {
        const indexA = sectionOrder.indexOf(a as ScrollableSectionEnum);
        const indexB = sectionOrder.indexOf(b as ScrollableSectionEnum);
        return indexA - indexB;
      }),
  ) as Record<ScrollableSectionEnum, boolean>;

  const sections: ScrollableSectionEnum[] = Object.entries(visibleSections)
    .filter(([, sectionConfig]) => sectionConfig)
    .map(([section]) => section as ScrollableSectionEnum);

  return {
    sections,
    visibleSections,
  };
};
